import {
  TickCircle,
  CloseCircle,
  Edit,
  ArrowLeft2,
  ArrowRight2,
} from 'iconsax-react'
import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  memo,
} from 'react'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import type {
  BatchValidationResult,
  RowValidationResult,
} from '@/utils/batchFileValidation'
import {
  BATCH_REQUIRED_FIELDS,
  batchRowSchema,
} from '@/utils/batchFileValidation'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

interface DataEditorTableProps {
  validationResult: BatchValidationResult
  editedData: Record<string, string>
  onDataChange: (editedData: Record<string, string>) => void
  onValidationStatusChange?: (
    hasErrors: boolean,
    totalErrors: number,
    validRows: number,
    validRowIndexes?: number[],
  ) => void
}

interface EditingState {
  rowIndex: number
  field: string
  value: string
}

const validateSingleField = (
  fieldName: string,
  value: string,
): string | null => {
  try {
    const testData: any = {}

    BATCH_REQUIRED_FIELDS.forEach((field) => {
      if (field === fieldName) {
        testData[field] = value
      } else {
        switch (field) {
          case 'subject-email':
            testData[field] = 'dummy@example.com'
            break
          case 'subject-age-approx':
            testData[field] = '25'
            break
          default:
            testData[field] = 'dummy'
        }
      }
    })

    testData[fieldName] = value

    const result = batchRowSchema.safeParse(testData)

    if (!result.success) {
      const fieldError = result.error.errors.find(
        (err) => err.path.length > 0 && err.path[0] === fieldName,
      )
      return fieldError ? fieldError.message : null
    }

    return null
  } catch (error) {
    return null
  }
}

const validateRowWithEditedData = (
  result: RowValidationResult,
  editedData: Record<string, string>,
  allFields: string[],
): {
  isValid: boolean
  fieldErrors: Array<{ field: string; error: string }>
} => {
  try {
    const rowData: any = {}

    allFields.forEach((field) => {
      const key = `${result.rowIndex}-${field}`
      const editedValue = editedData[key]
      const originalValue = result.data[field] || ''
      const finalValue = editedValue !== undefined ? editedValue : originalValue
      rowData[field] = finalValue
    })

    if (
      rowData['focused-topics'] &&
      rowData['focused-topics'].split(/[,;]/).length > 5
    ) {
      return {
        isValid: false,
        fieldErrors: [
          { field: 'focused-topics', error: 'Max 5 topics allowed' },
        ],
      }
    }

    const validationResult = batchRowSchema.safeParse(rowData)

    if (!validationResult.success) {
      const fieldErrors = validationResult.error.errors.map((error) => ({
        field: error.path.join('.'),
        error: error.message,
      }))
      return { isValid: false, fieldErrors }
    }

    return { isValid: true, fieldErrors: [] }
  } catch (error) {
    return { isValid: false, fieldErrors: [] }
  }
}

const DataCell = memo(
  ({
    result,
    field,
    value,
    originalFieldError,
    isRequired,
    isEditing,
    onEdit,
    onSave,
    onCancel,
  }: {
    result: RowValidationResult
    field: string
    value: string
    originalFieldError: any
    isRequired: boolean
    isEditing: boolean
    onEdit: () => void
    onSave: (value: string) => void
    onCancel: () => void
  }) => {
    const [inputValue, setInputValue] = useState(value)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (isEditing) {
        setInputValue(value)
        requestAnimationFrame(() => {
          inputRef.current?.focus()
          if (value) {
            inputRef.current?.select()
          }
        })
      }
    }, [isEditing, value])

    const wasEdited = value !== (result.data[field] || '')

    const currentFieldError = wasEdited
      ? validateSingleField(field, value)
      : originalFieldError?.error

    const hasError = !!currentFieldError
    const isEmpty = !value || value.toString().trim() === ''

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        onSave(inputValue)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }

    const handleBlur = () => {
      onSave(inputValue)
    }

    if (isEditing) {
      return (
        <div className="w-full h-full p-1">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="w-full h-full border-2 border-primaryColor focus:border-primaryColor bg-baseColorHighlight text-neutralColor"
          />
        </div>
      )
    }

    const cellClass = `
      w-full h-full p-2 cursor-pointer border-r border-b border-neutralColor5
      flex items-center justify-between hover:bg-baseColor transition-colors group
      ${hasError ? 'bg-baseColor border-l-4 border-l-dynamicRed' : ''}
      ${
        isEmpty && isRequired
          ? 'bg-baseColor border-l-4 border-l-dynamicYellow'
          : ''
      }
      ${
        !hasError && !isEmpty
          ? 'bg-baseColorHighlight border-l-4 border-l-dynamicGreen'
          : 'bg-baseColorHighlight'
      }
    `

    return (
      <div className={cellClass} onClick={onEdit}>
        <span
          className={`text-sm truncate flex-1 ${
            isEmpty ? 'text-neutralColor60 italic' : 'text-neutralColor'
          }`}
        >
          {isEmpty ? (isRequired ? 'Required' : 'Optional') : value}
        </span>
        <div className="flex items-center gap-1 ml-2">
          {isEmpty && isRequired ? (
            <div className="w-3 h-3 rounded-full bg-dynamicYellow flex items-center justify-center">
              <span className="text-staticBlack text-xs font-bold">!</span>
            </div>
          ) : hasError ? (
            <CloseCircle size="14" color={weCheckPalette.dynamicRed} />
          ) : !isEmpty ? (
            <TickCircle size="14" color={weCheckPalette.dynamicGreen} />
          ) : null}
          <Edit
            size="12"
            color={weCheckPalette.neutralColor60}
            className="opacity-0 group-hover:opacity-100"
          />
        </div>
      </div>
    )
  },
)

export const DataEditorTable: React.FC<DataEditorTableProps> = ({
  validationResult,
  editedData,
  onDataChange,
  onValidationStatusChange,
}) => {
  const { t } = useTranslation()

  const [localEditedData, setLocalEditedData] =
    useState<Record<string, string>>(editedData)
  const [editingState, setEditingState] = useState<EditingState | null>(null)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    setLocalEditedData(editedData)
  }, [editedData])

  useEffect(() => {
    onDataChange(localEditedData)
  }, [localEditedData, onDataChange])
  const allFields = useMemo(() => {
    const fieldsSet = new Set<string>()
    validationResult.detailedResults.forEach((result) => {
      Object.keys(result.data).forEach((field) => fieldsSet.add(field))
    })
    return Array.from(fieldsSet).sort()
  }, [validationResult.detailedResults])

  const dynamicValidationStatus = useMemo(() => {
    let totalErrorRows = 0
    let validRows = 0
    const validRowIndexes: number[] = []

    validationResult.detailedResults.forEach((result) => {
      const rowValidation = validateRowWithEditedData(
        result,
        localEditedData,
        allFields,
      )

      // Also check for empty required fields
      let hasEmptyRequiredFields = false

      BATCH_REQUIRED_FIELDS.forEach((field) => {
        const key = `${result.rowIndex}-${field}`
        const editedValue = localEditedData[key]
        const originalValue = result.data[field] || ''
        const finalValue =
          editedValue !== undefined ? editedValue : originalValue

        if (!finalValue || finalValue.trim() === '') {
          hasEmptyRequiredFields = true
        }
      })

      if (rowValidation.isValid && !hasEmptyRequiredFields) {
        validRows++
        validRowIndexes.push(result.rowIndex)
      } else {
        totalErrorRows++
      }
    })

    const hasErrors = totalErrorRows > 0
    return { hasErrors, totalErrorRows, validRows, validRowIndexes }
  }, [validationResult.detailedResults, localEditedData, allFields])

  useEffect(() => {
    if (onValidationStatusChange) {
      onValidationStatusChange(
        dynamicValidationStatus.hasErrors,
        dynamicValidationStatus.totalErrorRows,
        dynamicValidationStatus.validRows,
        dynamicValidationStatus.validRowIndexes,
      )
    }
  }, [dynamicValidationStatus, onValidationStatusChange])

  const isRequiredField = useCallback((field: string): boolean => {
    return BATCH_REQUIRED_FIELDS.includes(field)
  }, [])

  const getFieldError = useCallback(
    (result: RowValidationResult, field: string) => {
      return result.fieldErrors.find((error) => error.field === field)
    },
    [],
  )

  const formatColumnHeader = useCallback((field: string): string => {
    return field
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }, [])

  const getFieldStatusText = useCallback(
    (field: string): { text: string; className: string } => {
      if (isRequiredField(field)) {
        return {
          text: t('dashboard.batchSearch.required', 'Required'),
          className: 'text-neutralColor font-medium',
        }
      }

      if (field === 'business-state') {
        return {
          text: t(
            'dashboard.batchSearch.requiredIfConsent',
            'Only required if consent is needed',
          ),
          className: 'text-neutralColor60',
        }
      }

      return {
        text: t('dashboard.batchSearch.optional', 'Optional'),
        className: 'text-neutralColor60',
      }
    },
    [t, isRequiredField],
  )

  const getCellValue = useCallback(
    (result: RowValidationResult, field: string): string => {
      const key = `${result.rowIndex}-${field}`
      return localEditedData[key] !== undefined
        ? localEditedData[key]
        : result.data[field] || ''
    },
    [localEditedData],
  )

  const handleCellEdit = useCallback(
    (rowIndex: number, field: string, currentValue: string) => {
      setEditingState({ rowIndex, field, value: currentValue })
    },
    [],
  )

  const handleCellSave = useCallback(
    (rowIndex: number, field: string, newValue: string) => {
      const key = `${rowIndex}-${field}`
      const updatedData = { ...localEditedData, [key]: newValue }
      setLocalEditedData(updatedData)
      setEditingState(null)
    },
    [localEditedData],
  )

  const handleCellCancel = useCallback(() => {
    setEditingState(null)
  }, [])

  const checkScrollabilities = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    const hasHorizontalOverflow = scrollWidth > clientWidth

    setCanScrollLeft(hasHorizontalOverflow && scrollLeft > 0)
    setCanScrollRight(
      hasHorizontalOverflow && scrollLeft < scrollWidth - clientWidth,
    )
  }, [])

  const scrollHorizontally = useCallback((direction: 'left' | 'right') => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 800
    const currentScroll = container.scrollLeft
    const newScroll =
      direction === 'left'
        ? Math.max(0, currentScroll - scrollAmount)
        : currentScroll + scrollAmount

    container.scrollTo({
      left: newScroll,
      behavior: 'smooth',
    })
  }, [])

  const tableWidth = 60 + 80 + allFields.length * 200
  const sidebarWidth = 240
  const padding = 32
  const cardPadding = 32
  const availableWidth = `calc(100vw - ${
    sidebarWidth + padding + cardPadding
  }px)`

  useEffect(() => {
    const timer = setTimeout(checkScrollabilities, 100)
    return () => clearTimeout(timer)
  }, [
    allFields.length,
    validationResult.detailedResults.length,
    checkScrollabilities,
  ])

  const tableStyle = {
    display: 'grid',
    gridTemplateColumns: `60px 80px repeat(${allFields.length}, 200px)`,
    gap: '0',
    minWidth: `${tableWidth}px`,
  }

  return (
    <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="p-4 border-b border-neutralColor5 flex-shrink-0">
        <h3 className="text-lg font-semibold text-neutralColor">
          {t('dashboard.batchSearch.dataEditor', 'Data Editor')}
        </h3>
        <p className="text-sm text-neutralColor60 mt-1">
          {t(
            'dashboard.batchSearch.clickToEdit',
            'Click any cell to edit its value',
          )}
        </p>
      </div>

      <div className="flex-1 min-h-0 relative">
        {/* Fixed position scroll indicators */}
        {canScrollLeft && (
          <button
            onClick={() => scrollHorizontally('left')}
            className="fixed z-30 
                       bg-primaryColor text-staticWhite p-3 rounded-full shadow-lg 
                       hover:bg-primaryColor60 transition-colors select-none"
            style={{
              left: '280px',
              top: '70%',
              transform: 'translateY(-50%)',
            }}
            title={t('dashboard.batchSearch.scrollLeft', 'Scroll left')}
          >
            <ArrowLeft2 size="20" />
          </button>
        )}

        {canScrollRight && (
          <button
            onClick={() => scrollHorizontally('right')}
            className="fixed z-30 
                       bg-primaryColor text-staticWhite p-3 rounded-full shadow-lg 
                       hover:bg-primaryColor60 transition-colors select-none"
            style={{
              right: '40px',
              top: '70%',
              transform: 'translateY(-50%)',
            }}
            title={t('dashboard.batchSearch.scrollRight', 'Scroll right')}
          >
            <ArrowRight2 size="20" />
          </button>
        )}

        {/* Scrollable content */}
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-auto"
          onScroll={checkScrollabilities}
          style={{
            maxWidth: availableWidth,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <div
            style={tableStyle}
            className="bg-baseColorHighlight [&::-webkit-scrollbar]:hidden border-l border-neutralColor5"
          >
            {/* Header Row */}
            <div className="bg-baseColorHighlight border-r border-b border-neutralColor5 p-2 font-semibold text-center sticky top-0 z-10">
              {t('dashboard.batchSearch.row', 'Row')}
            </div>
            <div className="bg-baseColorHighlight border-r border-b border-neutralColor5 p-2 font-semibold text-center sticky top-0 z-10">
              {t('dashboard.batchSearch.status', 'Status')}
            </div>
            {allFields.map((field) => (
              <div
                key={field}
                className="bg-baseColorHighlight border-r border-b border-neutralColor5 p-2 font-semibold text-left sticky top-0 z-10"
              >
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-neutralColor">
                    {formatColumnHeader(field)}
                  </span>
                  <span
                    className={`text-xs font-normal ${
                      getFieldStatusText(field).className
                    }`}
                  >
                    {getFieldStatusText(field).text}
                  </span>
                </div>
              </div>
            ))}

            {/* Data Rows */}
            {validationResult.detailedResults.map((result) => {
              const currentRowValidation = validateRowWithEditedData(
                result,
                localEditedData,
                allFields,
              )
              const currentIsValid = currentRowValidation.isValid

              return (
                <React.Fragment key={result.rowIndex}>
                  {/* Row Number */}
                  <div className="bg-baseColorHighlight border-r border-b border-neutralColor5 p-2 text-center font-medium text-neutralColor">
                    {result.rowIndex - 1}
                  </div>

                  {/* Status - Dynamic based on current edited data */}
                  <div className="bg-baseColorHighlight border-r border-b border-neutralColor5 p-2 flex items-center justify-center gap-1">
                    {currentIsValid ? (
                      <TickCircle
                        size="16"
                        color={weCheckPalette.dynamicGreen}
                      />
                    ) : (
                      <CloseCircle
                        size="16"
                        color={weCheckPalette.dynamicRed}
                      />
                    )}
                    <span className="text-xs font-medium text-neutralColor">
                      {currentIsValid ? 'OK' : 'ERR'}
                    </span>
                  </div>

                  {/* Data Cells */}
                  {allFields.map((field) => {
                    const cellValue = getCellValue(result, field)
                    const originalFieldError = getFieldError(result, field)
                    const isEditing =
                      editingState?.rowIndex === result.rowIndex &&
                      editingState?.field === field

                    return (
                      <DataCell
                        key={`${result.rowIndex}-${field}`}
                        result={result}
                        field={field}
                        value={cellValue}
                        originalFieldError={originalFieldError}
                        isRequired={isRequiredField(field)}
                        isEditing={isEditing}
                        onEdit={() =>
                          handleCellEdit(result.rowIndex, field, cellValue)
                        }
                        onSave={(newValue) =>
                          handleCellSave(result.rowIndex, field, newValue)
                        }
                        onCancel={handleCellCancel}
                      />
                    )
                  })}
                </React.Fragment>
              )
            })}
          </div>
        </div>
      </div>
    </Card>
  )
}
